// PARTNER-PORTÁL · Fejlesztések — a KIHIRDETETT és PUBLIKUS bejegyzések: „Mi újság" (változásnapló) és
// „Min dolgozunk" (útiterv). A partner-felület design-nyelvén (kártyák, `Card`/`Text`/`Badge`, nincs
// adat-tábla): a partner nem adminisztrál, hanem OLVAS — ezért idővonal-szerű kártya-lista, nem lista-oldal.
//
// AMIT A FELÜLET KIMOND (nem hallgat el):
//  • ha a modul nem szolgálható ki (nincs gépi Odin-kredenciál), MAGYARÁZAT jön — nem üres lista, ami
//    „még nem fejlesztünk semmit"-ként hazudna;
//  • az útiterv tételei mellett a HORIZONT (Most / Következik / Később) áll, dátum SOSEM: pontos határidőt
//    a partnernek nem ígérünk (a szerver sem tárol ilyet ezen az úton).
//
// A KÉPEK: a törzsben a stabil, belső kép-út áll, amit a partner a SAJÁT, SZŰK végpontján olt fel friss
// presigned URL-re (`/portal/product-updates/images/:fileId` — az csak publikus bejegyzés törzs-képére ad
// URL-t). MIÉRT NEM a BFF írja bele a kész URL-t: a web-oldali szanitáló minden `<img>`-et eldob, aminek a
// `src`-je nem a stabil út (`sanitize-html.ts` hook) — az előre feloldott URL tehát csendben eltűnt volna.
import type { PortalProductUpdateRow } from '@atlas/contracts';
import { PORTAL_PERMISSIONS } from '@atlas/shared';
import {
  NewspaperIcon,
  QuestionMarkCircleIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type ComponentType, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalErrorState } from '@/components/portal/portal-error-state';
import { PortalPermissionGate } from '@/components/portal/portal-permission-gate';
import { ProductUpdateTypeBadge } from '@/components/product-updates/product-update-type-badge';
import { badgeVisual } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { usePortalHead } from '@/features/portal/portal-context';
import {
  HORIZON_COLUMNS,
  toBoardColumns,
  toTimelineGroups,
  unkeyedItems,
} from '@/features/product-updates/views';
import { api } from '@/lib/api';
import { DEFAULT_TIME_ZONE, formatDateTime, localeFor } from '@/lib/format-datetime';

/**
 * A PARTNER kép-feloldója — a szűk portál-végpont. A `scope` a cache-kulcs része: ugyanarra a fájlra a belső
 * és a partner-felület MÁS URL-t kap (más token, más presign), tehát nem oszthatják a cache-t.
 */
const PORTAL_IMAGE_RESOLVER = {
  scope: 'portal-product-updates',
  resolve: async (fileId: string): Promise<string | null> => {
    const res = await api.portal.productUpdateImage.query({ params: { fileId } });
    return res.status === 200 ? res.body.url : null;
  },
};

export const Route = createFileRoute('/portal/product-updates')({
  component: PortalProductUpdatesPage,
});

/** A lap a jog-kapun át: jog nélkül magyarázat áll a lap helyett (a fal a BFF-ben van). */
function PortalProductUpdatesPage(): JSX.Element {
  return (
    <PortalPermissionGate permission={PORTAL_PERMISSIONS.productUpdatesView}>
      <PortalProductUpdatesPageBody />
    </PortalPermissionGate>
  );
}

function PortalProductUpdatesPageBody(): JSX.Element {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'hu';
  // A nézet LOKÁLIS állapot, nem URL: a partner-portálon nincs mély-linkelt lista-nézet, és egy
  // `?view=` paraméter a megosztott linkeket is szennyezné.
  const [view, setView] = useState<'news' | 'roadmap'>('news');

  usePortalHead(
    { title: t('portal.nav.productUpdates'), subtitle: t('portal.productUpdates.subtitle') },
    [t('portal.nav.productUpdates')],
  );

  const q = useQuery({
    queryKey: portalKeys.productUpdates(lang),
    queryFn: () => api.portal.productUpdates.query({ query: { lang } }),
  });
  const body = q.data?.status === 200 ? q.data.body : undefined;
  const isForbidden = q.data?.status === 403;
  const isError = !q.isLoading && q.data !== undefined && q.data.status !== 200 && !isForbidden;

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }
  // A 403 a partner-felületen NEM hiba-állapot, hanem SAJÁT állapot („nincs portál-hozzáférése") — a
  // többi portál-lap mintája (`portal.invoices`): `EmptyState`, „Újra" gomb nélkül.
  if (isForbidden) {
    return (
      <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
    );
  }
  if (isError) return <PortalErrorState onRetry={() => void q.refetch()} />;
  // KI SEM SZOLGÁLHATÓ állapot (konfiguráció) — magyarázat, „Újra" gomb NÉLKÜL: az újratöltés nem segít.
  if (body?.unavailable === true) {
    return (
      <EmptyState
        title={t('portal.productUpdates.unavailableTitle')}
        description={t('portal.productUpdates.unavailableDesc')}
      />
    );
  }

  const changelog = body?.changelog ?? [];
  const roadmap = body?.roadmap ?? [];
  const unkeyedHorizon = unkeyedItems(roadmap, HORIZON_COLUMNS, (r) => r.horizon);

  return (
    <div className="space-y-4">
      {/* A KÉT NÉZET a partner KÉT KÉRDÉSE: „mi újság?" és „min dolgoztok?". Eddig egymás alatt, két
          szekcióban álltak — így a hosszú változásnapló alá temette az útitervet. Nézet-váltóként
          mindkettő a saját, neki való alakját kapja: a hírfolyam IDŐVONALAT, az útiterv BOARDOT.

          A felirat a partner nyelvén szól: se „board", se „timeline" — „Mi újság" és „Min dolgozunk". */}
      <Tabs value={view} onValueChange={(v) => setView(v === 'roadmap' ? 'roadmap' : 'news')}>
        <TabsList>
          <TabsTrigger value="news">
            <NewspaperIcon />
            {t('portal.productUpdates.changelogTitle')}
          </TabsTrigger>
          <TabsTrigger value="roadmap">
            <RocketLaunchIcon />
            {t('portal.productUpdates.roadmapTitle')}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === 'news' ? (
        changelog.length === 0 ? (
          <EmptyState
            title={t('portal.productUpdates.changelogEmpty')}
            description={t('portal.productUpdates.changelogEmptyDesc')}
          />
        ) : (
          // IDŐVONAL: hónap-csoportokkal, a legfrissebb elöl — a csoport-fejléc az, ami a görgetés közben
          // elhelyez az időben.
          <div className="space-y-6">
            {toTimelineGroups(changelog).map((group) => (
              <section key={group.month || 'no-date'}>
                <div className="bg-background/95 sticky top-0 z-10 flex items-center gap-3 py-1.5 backdrop-blur">
                  <Text variant="overline" as="h2" className="shrink-0">
                    {group.month === ''
                      ? t('portal.productUpdates.noDate')
                      : formatDateTime(`${group.month}-15T12:00:00.000Z`, lang, DEFAULT_TIME_ZONE, {
                          year: 'numeric',
                          month: 'long',
                        })}
                  </Text>
                  <span className="bg-border h-px flex-1" aria-hidden />
                </div>
                <div className="mt-3 space-y-3">
                  {group.items.map((item) => (
                    <UpdateCard key={item.id} item={item} lang={lang} showDate />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )
      ) : roadmap.length === 0 ? (
        <EmptyState
          title={t('portal.productUpdates.roadmapEmpty')}
          description={t('portal.productUpdates.roadmapEmptyDesc')}
        />
      ) : (
        <div className="space-y-3">
          <Text tone="tertiary">{t('portal.productUpdates.roadmapHelp')}</Text>
          {/* A PARTNER-BOARD oszlopai az IDŐHORIZONT — Most / Következik / Később —, SOHA nem a belső
              fázis. A belső fázis-nevek kiszivárogtatnák a folyamatot és elvárást szülnének; a
              Most/Következik/Később a nyilvános útiterv bevett formája, épp azért, mert nem ígér dátumot.

              A HORIZONT NÉLKÜLI tételek NEGYEDIK, UTOLSÓ oszlopot kapnak — nem külön szekciót a board alatt:
              a `horizon` nullázható, tehát egy kihirdetett, publikus bejegyzés oszlop nélkül maradna, és
              némán eltűnne (élő e2e-n mérve). Külön szekcióként viszont jobban elkülönült, mint amennyire
              indokolt: ugyanolyan tétel, csak nincs időzítése.

              MOBILON NINCS BOARD: az oszlopok EGYMÁS ALÁ kerülnek, és az oszlop-fejléc szakasz-címmé válik
              (Most / Következik / Később). Egy 375 px-es telefonon a vízszintes board a 752 px-es tartalmából
              343-at mutatott — a következő oszlop levágva, és a kártyák is keskenyek. A belső lap ugyanezt a
              szabályt követi, csak ott a board FÜL tiltott mobilon; itt nem lehet, mert a két fül KÉT KÜLÖN
              halmaz (kiadott vs. tervezett), tehát a fül letiltása magát az útitervet venné el telefonról.

              `sm`-től fölfelé a board visszaáll: oszlopok egymás mellett, vízszintes görgetéssel, ugyanaz az
              alak, mint a belső boardon (`product-update-board.tsx`), a `w-60` alsó határral. A váltás CSAK
              CSS — nincs JS-breakpoint, tehát átméretezésre is azonnal követi magát, és nincs hidratálási rés. */}
          <div className="flex flex-col gap-6 sm:flex-row sm:gap-4 sm:overflow-x-auto sm:pb-1">
            {[
              ...toBoardColumns(roadmap, HORIZON_COLUMNS, (r) => r.horizon).map((col) => ({
                key: col.key,
                icon: badgeVisual('productUpdateHorizon', col.key)?.icon,
                label: t(`productUpdates.horizon.${col.key}`),
                items: col.items,
              })),
              ...(unkeyedHorizon.length > 0
                ? [
                    {
                      key: 'no-horizon',
                      icon: QuestionMarkCircleIcon,
                      label: t('portal.productUpdates.noHorizon'),
                      items: unkeyedHorizon,
                    },
                  ]
                : []),
            ].map((col) => (
              <section key={col.key} className="space-y-3 sm:w-60 sm:shrink-0 sm:grow">
                <ColumnHeader icon={col.icon} label={col.label} count={col.items.length} />
                {col.items.length === 0 ? (
                  <Text tone="tertiary" className="text-xs">
                    {t('portal.productUpdates.columnEmpty')}
                  </Text>
                ) : (
                  col.items.map((item) => <UpdateCard key={item.id} item={item} lang={lang} />)
                )}
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * A board OSZLOP-FEJLÉCE — ugyanaz az alak, mint a belső boardon (`product-update-board.tsx`): ikon +
 * overline-cím + darabszám-jelvény. SZÁNDÉKOSAN nem `DomainBadge`: a fejléc nem a tétel JELE, hanem a HELY
 * neve, ahová a kártya kerül — jelvényként ugyanolyan súlyú lett volna, mint a kártyákon álló típus-jelvények.
 */
function ColumnHeader({
  icon: Icon,
  label,
  count,
}: {
  icon?: ComponentType<SVGProps<SVGSVGElement>> | undefined;
  label: string;
  count: number;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      {Icon !== undefined && <Icon className="text-subtle size-4 shrink-0" aria-hidden />}
      <Text variant="overline" as="h2">
        {label}
      </Text>
      <Badge variant="secondary">{count}</Badge>
    </div>
  );
}

/** Egy bejegyzés kártyája. A `showDate` a változásnaplón a kiadás dátumát mutatja, az útitervben a horizontot. */
function UpdateCard({
  item,
  lang,
  showDate = false,
}: {
  item: PortalProductUpdateRow;
  lang: 'hu' | 'en';
  showDate?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card className="card-glass space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <ProductUpdateTypeBadge type={item.type} label={t(`productUpdates.type.${item.type}`)} />
        {showDate && item.shippedAt ? (
          <Text tone="tertiary" className="tabular-nums">
            {new Date(item.shippedAt).toLocaleDateString(localeFor(lang))}
          </Text>
        ) : null}
        {/* A HORIZONT-jelvény a kártyáról LEKERÜLT: az útitervben az OSZLOP mondja ki a horizontot, a
            kártyán megismételve kétszer állna ugyanaz. A cél-időszak marad — az oszlopon belül ez az
            egyetlen finomabb időbeli jelzés (dátumot továbbra sem ígérünk). */}
        {!showDate && item.targetPeriod ? <Text tone="tertiary">{item.targetPeriod}</Text> : null}
      </div>

      <Text variant="itemTitle" as="h3">
        {item.title}
      </Text>
      {/* A TÖRZS hordozza a képeket is (a szerkesztő szúrja be őket) — külön kép-galéria nincs (user-döntés).
          A feloldás a PARTNER-végponton megy, nem a belsőn. */}
      {item.body ? (
        <RichTextContent html={item.body} imageResolver={PORTAL_IMAGE_RESOLVER} />
      ) : null}
    </Card>
  );
}
